/**
 * Admin FormWrapper
 *
 * One DOM scanner owns mutating admin forms and creates per-form Form instances.
 * Page controllers may still call legacy init helpers; they delegate here.
 */

import { Form, normalizeFormResponse } from "./Form";
import { Modal } from "../utils/Modal";

export type AdminFormKind = "default" | "toggle-switch" | "segmented-control";

const SELECTOR = [
  'form[data-admin-ajax="1"]',
  "form.admin-toggle-switch-form",
  "form.admin-segmented-control-form",
].join(",");

const LOADER_SELECTOR = ".admin-form__loader";
const TOGGLE_LOAD_TEXT = "처리 중...";
const TOGGLE_HINT_ON = "켜짐";
const TOGGLE_HINT_OFF = "꺼짐";

function getFormKind(form: HTMLFormElement): AdminFormKind {
  const explicit = form.dataset.adminFormKind;
  if (explicit === "toggle-switch" || explicit === "segmented-control" || explicit === "default") {
    return explicit;
  }
  if (form.classList.contains("admin-toggle-switch-form")) return "toggle-switch";
  if (form.classList.contains("admin-segmented-control-form")) return "segmented-control";
  return "default";
}

function isExcluded(form: HTMLFormElement): boolean {
  if (form.dataset.adminNative === "1") return true;
  if (form.hasAttribute("data-confirm-redirect")) return true;
  if (form.classList.contains("admin-auth__form")) return true;
  return (form.getAttribute("method") || "GET").toUpperCase() === "GET";
}

function getRedirect(data: Record<string, unknown>): string | undefined {
  const nested = data.data;
  const r = data.redirect ?? (nested && typeof nested === "object" ? (nested as Record<string, unknown>).redirect : undefined);
  return typeof r === "string" ? r : undefined;
}

function dispatchUnreadChanged(data: Record<string, unknown>): void {
  const payload = ((data?.data as Record<string, unknown> | undefined) ?? data) as Record<string, unknown>;
  window.dispatchEvent(
    new CustomEvent("admin:notifications:unread-changed", { detail: payload }),
  );
}

function handleDefaultSuccess(form: HTMLFormElement, data: Record<string, unknown>): void {
  const action = form.dataset.successAction ?? "";
  const redirect = getRedirect(data);

  if (action === "remove-row") {
    const row = form.closest("tr");
    if (row) {
      const rowId = row.dataset.rowId ?? row.dataset.id ?? "";
      if (rowId) {
        const wrap = row.closest<HTMLElement>("[data-listing-table], [data-table-manager]");
        wrap?.querySelector<HTMLElement>(`.admin-table-card[data-row-id="${rowId}"]`)?.remove();
      }
      const tbody = row.parentElement;
      row.remove();
      if (tbody && !tbody.querySelector("tr")) {
        const wrap = tbody.closest<HTMLElement>("[data-listing-table], [data-table-manager]");
        wrap?.classList.add("is-empty");
        const emptyState = wrap?.querySelector<HTMLElement>("[data-empty-state]");
        if (emptyState) emptyState.hidden = false;
      }
    }
    return;
  }

  if (action === "notification-mark-read") {
    const item = form.closest<HTMLElement>(
      ".admin-header__notifications-item, .admin-notifications__item",
    );
    item?.classList.remove("is-unread", "admin-notifications__item--unread");
    item?.querySelector<HTMLElement>(".admin-notifications__unread-dot")?.remove();
    form.remove();
    dispatchUnreadChanged(data);
    return;
  }

  if (action === "notification-mark-all-read") {
    document
      .querySelectorAll<HTMLElement>(
        ".admin-header__notifications-item.is-unread, .admin-notifications__item--unread",
      )
      .forEach((el) => {
        el.classList.remove("is-unread", "admin-notifications__item--unread");
      });
    document
      .querySelectorAll<HTMLElement>(
        ".admin-header__notifications-item-mark, .admin-notifications__mark-form, .admin-notifications__unread-dot",
      )
      .forEach((el) => el.remove());
    form.remove();
    dispatchUnreadChanged(data);
    return;
  }

  if (action === "reload") {
    window.location.reload();
    return;
  }

  if (action === "redirect" && redirect) {
    window.location.href = redirect;
    return;
  }

  if (redirect) {
    window.location.href = redirect;
  }
}

function updateToggleContainer(form: HTMLFormElement, enabled: boolean): void {
  const container = form.closest<HTMLElement>("[data-feature-key], [data-notif-type], .admin-features__item, .admin-notifications-config__item");
  container?.classList.toggle("is-on", enabled);
  container?.classList.toggle("is-off", !enabled);
  container
    ?.querySelector<HTMLElement>(".admin-features__item-state, .admin-notifications-config__item-state")
    ?.replaceChildren(document.createTextNode(enabled ? "사용 중" : "꺼짐"));
}

function bindToggleForm(form: HTMLFormElement): void {
  const button = form.querySelector<HTMLButtonElement>(".admin-toggle-switch");
  const hint = button?.querySelector<HTMLElement>(".admin-toggle-switch__hint") ?? null;

  const instance = new Form(form, {
    submitUrl: form.action || form.dataset.submitUrl || "",
    submitButtonSelector: ".admin-toggle-switch",
    toast: {
      id: `toggle-${form.action}`,
      loadingMessage: "처리 중...",
      successMessage: "변경했습니다.",
      errorMessage: "토글에 실패했습니다.",
    },
    validate: form.dataset.confirm
      ? async () => (await Modal.confirm(form.dataset.confirm ?? "")) ? null : ""
      : undefined,
    requestFailedMessage: "네트워크 오류가 발생했습니다.",
    invalidResponseMessage: "응답을 파싱할 수 없습니다.",
    onSubmittingChange(submitting) {
      if (!button) return;
      if (submitting) {
        button.dataset.prevHint = hint?.textContent ?? "";
        if (hint) hint.textContent = TOGGLE_LOAD_TEXT;
      } else if (hint && button.dataset.prevHint !== undefined) {
        hint.textContent = button.dataset.prevHint;
        delete button.dataset.prevHint;
      }
    },
    onSuccess(data) {
      const normalized = normalizeFormResponse(data);
      const enabled = Boolean(normalized.payload.enabled);
      button?.classList.toggle("is-on", enabled);
      button?.classList.toggle("is-off", !enabled);
      button?.setAttribute("aria-pressed", enabled ? "true" : "false");
      if (hint) hint.textContent = enabled ? TOGGLE_HINT_ON : TOGGLE_HINT_OFF;
      updateToggleContainer(form, enabled);

      const label = form
        .closest("[data-feature-key]")
        ?.querySelector(".admin-features__item-label")
        ?.textContent ?? "이 기능";
      if (form.closest("[data-feature-key]")) {
        if (enabled) {
          form.dataset.confirm = `'${label}' 을 끄면 관련 라우트가 404 가 됩니다. 계속할까요?`;
        } else {
          delete form.dataset.confirm;
        }
      }
    },
    onFail() {},
  });
  instance.init();
}

function currentSegmentedValue(form: HTMLFormElement, fallback = ""): string {
  return form.querySelector<HTMLInputElement>(".admin-segmented-control__input:checked")?.value ?? fallback;
}

function applySegmentedValue(form: HTMLFormElement, value: string): void {
  form.querySelectorAll<HTMLInputElement>(".admin-segmented-control__input").forEach((input) => {
    const selected = input.value === value;
    input.checked = selected;
    const label = form.querySelector<HTMLLabelElement>(`label[for="${input.id}"]`);
    label?.classList.toggle("is-selected", selected);
  });
}

function bindSegmentedForm(form: HTMLFormElement): void {
  const group = form.querySelector<HTMLElement>(".admin-segmented-control");
  if (!group) return;

  form.addEventListener("submit", (event) => event.preventDefault());
  form.dataset.scPrevValue = currentSegmentedValue(form);
  form.dataset.scInitialized = "1";

  const instance = new Form(form, {
    submitUrl: form.action || form.dataset.submitUrl || "",
    submitButtonSelector: 'button[type="submit"]',
    toast: true,
    validate: form.dataset.confirm
      ? async () => (await Modal.confirm(form.dataset.confirm ?? "")) ? null : ""
      : undefined,
    requestFailedMessage: "네트워크 오류가 발생했습니다.",
    invalidResponseMessage: "응답을 파싱할 수 없습니다.",
    onSubmittingChange(submitting) {
      group.classList.toggle("is-loading", submitting);
      form.querySelectorAll<HTMLInputElement>(".admin-segmented-control__input").forEach((input) => {
        input.disabled = submitting;
      });
    },
    onSuccess(data) {
      const normalized = normalizeFormResponse(data);
      const value = String(normalized.payload.value ?? currentSegmentedValue(form));
      applySegmentedValue(form, value);
      form.dataset.scPrevValue = value;
    },
    onFail() {
      applySegmentedValue(form, form.dataset.scPrevValue ?? "");
    },
  });
  instance.init();

  form.querySelectorAll<HTMLInputElement>(".admin-segmented-control__input").forEach((input) => {
    input.addEventListener("change", () => {
      if (!input.checked) return;
      const next = input.value;
      const prev = form.dataset.scPrevValue ?? currentSegmentedValue(form, next);
      if (prev === next && form.dataset.scInitialized === "1") return;
      if (typeof form.requestSubmit === "function") {
        form.requestSubmit();
      } else {
        form.dispatchEvent(new SubmitEvent("submit", { bubbles: true, cancelable: true }));
      }
    });
  });
}

function bindDefaultForm(form: HTMLFormElement): void {
  const instance = new Form(form, {
    submitUrl: form.action || form.dataset.submitUrl || "",
    loaderSelector: LOADER_SELECTOR,
    validate: form.dataset.confirm
      ? async () => (await Modal.confirm(form.dataset.confirm ?? "")) ? null : ""
      : undefined,
    requestFailedMessage: "요청에 실패했습니다.",
    invalidResponseMessage: "응답을 처리할 수 없습니다.",
    onSuccess(data) {
      handleDefaultSuccess(form, data);
    },
    onFail() {},
  });
  instance.init();
}

export function bindForm(form: HTMLFormElement): void {
  if (form.dataset.adminAjaxBound === "1") return;
  if (isExcluded(form)) return;
  const url = form.action || form.dataset.submitUrl || "";
  if (!url) return;

  form.dataset.adminAjaxBound = "1";
  const kind = getFormKind(form);
  form.dataset.adminFormKind = kind;

  if (kind === "toggle-switch") {
    bindToggleForm(form);
    return;
  }
  if (kind === "segmented-control") {
    bindSegmentedForm(form);
    return;
  }
  bindDefaultForm(form);
}

export function bindForms(scope: ParentNode = document): void {
  scope.querySelectorAll<HTMLFormElement>(SELECTOR).forEach((form) => {
    bindForm(form);
  });
}

export function initFormWrapper(scope: ParentNode = document): void {
  bindForms(scope);
}
